/**
 * Utility functions for parsing and organizing EnergyPlus results data
 */

import { energyConversion } from "utils/constants/energyConversion";
import { emissionFactorsNIR } from "utils/emissions/getEmissionFactors";
import { provinceCodeMap } from "utils/weather";

// List of result keys to ignore (uncommon/zero values)
const IGNORED_RESULT_PATTERNS = [
    "Grill",
    "Fireplace",
    "Pool",
    "Spa",
    "Well Pump",
    "Electric Vehicle",
    "PV",
    "Generator",
    "Battery",
    "Wood Pellets",
    "Wood Cord",
    "Coal",
    "Fuel Oil",
    "Propane",
    "Garage",
    "Preheating",
    "Precooling",
    "Solar Thermal",
    "Desuperheater",
    "Whole House Fan",
    "Ceiling Fan",
    "Television",
    "Dehumidifier",
    "Freezer",
];

/**
 * Check if a result key should be ignored
 */
export const shouldIgnoreResult = (key) => {
    return IGNORED_RESULT_PATTERNS.some((pattern) => key.includes(pattern));
};

/**
 * Parse annual results and extract key metrics
 */
export const parseAnnualResults = (annualResults = {}, regionId = 5) => {
    const regionCode = provinceCodeMap[regionId] || "ON";
    const results = {};

    // Total Energy Use
    results.totalEnergy = annualResults["Energy Use: Total (MBtu)"] || 0;
    results.netEnergy = annualResults["Energy Use: Net (MBtu)"] || 0;

    // Fuel Use Totals
    results.fuelUse = {
        electricity: annualResults["Fuel Use: Electricity: Total (MBtu)"] || 0,
        naturalGas: annualResults["Fuel Use: Natural Gas: Total (MBtu)"] || 0,
        fuelOil: annualResults["Fuel Use: Fuel Oil: Total (MBtu)"] || 0,
        propane: annualResults["Fuel Use: Propane: Total (MBtu)"] || 0,
        woodCord: annualResults["Fuel Use: Wood Cord: Total (MBtu)"] || 0,
        woodPellets: annualResults["Fuel Use: Wood Pellets: Total (MBtu)"] || 0,
        coal: annualResults["Fuel Use: Coal: Total (MBtu)"] || 0,
    };

    // End Use - Electricity
    results.endUseElectricity = {};
    Object.keys(annualResults).forEach((key) => {
        if (key.startsWith("End Use: Electricity:") && !shouldIgnoreResult(key)) {
            const endUseName = key.replace("End Use: Electricity: ", "").replace(" (MBtu)", "");
            const value = annualResults[key] || 0;
            if (value > 0.001) {
                results.endUseElectricity[`Electricity: ${endUseName}`] = value;
            }
        }
    });

    // End Use - Natural Gas
    results.endUseNaturalGas = {};
    Object.keys(annualResults).forEach((key) => {
        if (key.startsWith("End Use: Natural Gas:") && !shouldIgnoreResult(key)) {
            const endUseName = key.replace("End Use: Natural Gas: ", "").replace(" (MBtu)", "");
            const value = annualResults[key] || 0;
            if (value > 0.001) {
                results.endUseNaturalGas[`Natural Gas: ${endUseName}`] = value;
            }
        }
    });

    // Emissions - Calculated based on NIR emission factors
    const {
        elec: elecEf,
        ng: ngEf,
        oil: oilEf,
        propane: propaneEf,
        woodStove: woodStoveEf,
    } = emissionFactorsNIR[regionCode];

    const elecEmissions = (mbtuToGJ(results.fuelUse.electricity) / energyConversion.elecGJperkWh) * elecEf * 1e-6;
    const ngEmissions = (mbtuToGJ(results.fuelUse.naturalGas) / energyConversion.ngGJperm3) * ngEf * 1e-6;
    const oilEmissions = (mbtuToGJ(results.fuelUse.fuelOil) / energyConversion.oilGJperL) * oilEf * 1e-6;
    const propaneEmissions = (mbtuToGJ(results.fuelUse.propane) / energyConversion.propaneGJperL) * propaneEf * 1e-6;
    const woodStoveEmissions =
        ((mbtuToGJ(results.fuelUse.woodPellets) + mbtuToGJ(results.fuelUse.woodCord)) /
            energyConversion.woodPelletsGJperkg) *
        woodStoveEf *
        1e-6;

    const totalEmissions = elecEmissions + ngEmissions + oilEmissions + propaneEmissions + woodStoveEmissions;

    results.emissions = {
        total: totalEmissions || 0,
        net: annualResults["Emissions: CO2e: Total Emissions (CO2): Net (lb)"] || 0,
        electricity: elecEmissions || 0,
        naturalGas: ngEmissions || 0,
        fuelOil: oilEmissions || 0,
        propane: propaneEmissions || 0,
        wood: woodStoveEmissions || 0,
    };

    results.emissionsSource = `NIR 2025, ${regionCode}`;

    // Loads
    results.loads = {
        heating: annualResults["Load: Heating: Delivered (MBtu)"] || 0,
        cooling: annualResults["Load: Cooling: Delivered (MBtu)"] || 0,
        hotWater: annualResults["Load: Hot Water: Delivered (MBtu)"] || 0,
        hotWaterTankLosses: annualResults["Load: Hot Water: Tank Losses (MBtu)"] || 0,
    };

    // Unmet Hours
    results.unmetHours = {
        heating: annualResults["Unmet Hours: Heating (hr)"] || 0,
        cooling: annualResults["Unmet Hours: Cooling (hr)"] || 0,
    };

    // Peak Electricity
    results.peakElectricity = {
        winter: annualResults["Peak Electricity: Winter Total (W)"] || 0,
        summer: annualResults["Peak Electricity: Summer Total (W)"] || 0,
        annual: annualResults["Peak Electricity: Annual Total (W)"] || 0,
    };

    // Peak Loads
    results.peakLoads = {
        heating: annualResults["Peak Load: Heating: Delivered (kBtu/hr)"] || 0,
        cooling: annualResults["Peak Load: Cooling: Delivered (kBtu/hr)"] || 0,
    };

    // Hot Water Usage
    results.hotWaterUsage = {
        clothesWasher: annualResults["Hot Water: Clothes Washer (gal)"] || 0,
        dishwasher: annualResults["Hot Water: Dishwasher (gal)"] || 0,
        fixtures: annualResults["Hot Water: Fixtures (gal)"] || 0,
        distributionWaste: annualResults["Hot Water: Distribution Waste (gal)"] || 0,
    };

    // HVAC Capacity
    results.hvacCapacity = {
        heating: annualResults["HVAC Capacity: Heating (Btu/h)"] || 0,
        cooling: annualResults["HVAC Capacity: Cooling (Btu/h)"] || 0,
    };

    // HVAC Design Temperature
    results.hvacDesignTemp = {
        heating: annualResults["HVAC Design Temperature: Heating (F)"] || 0,
        cooling: annualResults["HVAC Design Temperature: Cooling (F)"] || 0,
    };

    return results;
};

/**
 * Get fuel use data for pie chart (only non-zero values, converted to GJ)
 */
export const getFuelUseChartData = (fuelUse) => {
    const data = [["Fuel type", "GJ"]];
    const fuels = [
        { key: "electricity", label: "Electricity" },
        { key: "naturalGas", label: "Natural Gas" },
        { key: "fuelOil", label: "Fuel Oil" },
        { key: "propane", label: "Propane" },
        { key: "woodPellets", label: "Wood Pellets" },
    ];

    fuels.forEach(({ key, label }) => {
        if (fuelUse[key] > 0.001) {
            data.push([label, mbtuToGJ(fuelUse[key])]);
        }
    });

    return data.length > 1 ? data : null;
};

/**
 * Clean and normalize end use labels, grouping similar items together
 */
const cleanEndUseLabel = (label) => {
    // Remove fuel type prefix if present
    let cleaned = label.replace(/^(Electricity|Natural Gas):\s*/, "");

    // Group heating-related items
    if (cleaned.includes("Heating")) {
        return "Heating";
    }

    // Group cooling-related items
    if (cleaned.includes("Cooling")) {
        return "Cooling";
    }

    // Group hot water-related items
    if (cleaned.includes("Hot Water")) {
        return "Hot Water";
    }

    // Group all lighting types
    if (cleaned.includes("Lighting")) {
        return "Lighting";
    }

    // Group all appliances
    const appliances = ["Refrigerator", "Freezer", "Dishwasher", "Clothes Washer", "Clothes Dryer", "Range/Oven"];
    if (appliances.some((appliance) => cleaned.includes(appliance))) {
        return "Appliances";
    }

    // Specific mappings for other items
    const specificMappings = {
        "Mech Vent": "Mechanical Ventilation",
        "Plug Loads": "Plug Loads",
    };

    // Check for exact match in specific mappings
    if (specificMappings[cleaned]) {
        return specificMappings[cleaned];
    }

    // Return cleaned label if no match
    return cleaned;
};

/**
 * Get end use data for pie chart (combined electricity and natural gas)
 */
export const getEndUseChartData = (endUseElectricity, endUseNaturalGas) => {
    const endUseMap = {};

    // Combine electricity end uses
    Object.keys(endUseElectricity).forEach((key) => {
        const cleanKey = cleanEndUseLabel(key);
        if (!endUseMap[cleanKey]) {
            endUseMap[cleanKey] = 0;
        }
        endUseMap[cleanKey] += endUseElectricity[key];
    });

    // Combine natural gas end uses
    Object.keys(endUseNaturalGas).forEach((key) => {
        const cleanKey = cleanEndUseLabel(key);
        if (!endUseMap[cleanKey]) {
            endUseMap[cleanKey] = 0;
        }
        endUseMap[cleanKey] += endUseNaturalGas[key];
    });

    // Convert to chart data format, sorted by value (converted to GJ)
    const data = [["End Use", "GJ"]];
    Object.keys(endUseMap)
        .sort((a, b) => endUseMap[b] - endUseMap[a])
        .forEach((key) => {
            if (endUseMap[key] > 0.001) {
                data.push([key, mbtuToGJ(endUseMap[key])]);
            }
        });

    return data.length > 1 ? data : null;
};

/**
 * Get emissions data for pie chart (only non-zero values, values already converted to tCO₂ and using NIR emission factors)
 */
export const getEmissionsChartData = (emissions) => {
    const data = [["Fuel type", "tCO₂"]];
    const fuelEmissions = [
        { key: "electricity", label: "Electricity" },
        { key: "naturalGas", label: "Natural Gas" },
        { key: "fuelOil", label: "Fuel Oil" },
        { key: "propane", label: "Propane" },
        { key: "wood", label: "Wood Pellets" },
    ];

    fuelEmissions.forEach(({ key, label }) => {
        if (emissions[key] > 0.001) {
            data.push([label, emissions[key]]);
        }
    });

    return data.length > 1 ? data : null;
};

/**
 * Convert MBtu to GJ
 */
export const mbtuToGJ = (mbtu) => {
    return mbtu * 1.0550558526; // 1 MBtu = 1.055056 GJ
};

/**
 * Convert lb to metric tons
 */
export const lbToMetricTons = (lb) => {
    return lb / 2204.62; // 1 metric ton = 2204.62 lb
};

/**
 * Get available hourly metrics from usage_data and loads_temps_data
 */
export const getAvailableHourlyMetrics = (usageData = {}, loadsTempsData = {}) => {
    const metrics = [];

    // Add usage_data metrics
    Object.keys(usageData).forEach((key) => {
        metrics.push({
            value: `usage:${key}`,
            label: key,
            source: "usage",
        });
    });

    // Add loads_temps_data metrics
    Object.keys(loadsTempsData).forEach((key) => {
        metrics.push({
            value: `loads:${key}`,
            label: key,
            source: "loads",
        });
    });

    return metrics;
};

/**
 * Convert kBtu to kWh
 */
export const kBtuToKWh = (kBtu) => {
    return kBtu * 0.293071; // 1 kBtu = 0.293071 kWh
};

/**
 * Convert Fahrenheit to Celsius
 */
export const fahrenheitToCelsius = (f) => {
    return ((f - 32) * 5) / 9;
};

/**
 * Get unit for a metric based on its label (returns desired unit)
 */
export const getMetricUnit = (label) => {
    if (label.includes("Temperature") || label.includes("Drybulb")) {
        return "°C";
    }
    if (label.includes("Load") && label.includes("Delivered")) {
        return "kWh";
    }
    if (label.includes("Fuel Use") || label.includes("End Use")) {
        return "kWh";
    }
    return "";
};

/**
 * Check if a metric needs unit conversion and get conversion function
 */
export const needsConversion = (label, source) => {
    // Temperature fields need F to C conversion
    if (label.includes("Temperature") || label.includes("Drybulb")) {
        return { convert: fahrenheitToCelsius, initialUnit: "°F", desiredUnit: "°C" };
    }
    // Load fields need kBtu to kWh conversion
    if (label.includes("Load") && label.includes("Delivered")) {
        return { convert: kBtuToKWh, initialUnit: "kBtu", desiredUnit: "kWh" };
    }
    // usage_data fields are already in kWh, no conversion needed
    if (source === "usage") {
        return { convert: null, initialUnit: "kWh", desiredUnit: "kWh" };
    }
    return { convert: null, initialUnit: "", desiredUnit: "" };
};

/**
 * Format hourly data for Google Charts with dual y-axes
 * Applies unit conversions as needed (kBtu to kWh, F to C)
 */
export const formatHourlyChartData = (
    usageData = {},
    loadsTempsData = {},
    leftMetric = null,
    rightMetric = null,
    startTime = null
) => {
    if (!leftMetric && !rightMetric) {
        return null;
    }

    // Generate hour labels (0-8759)
    const hours = Array.from({ length: 8760 }, (_, i) => i);

    // Parse metrics
    const leftKey = leftMetric?.replace(/^(usage|loads):/, "");
    const rightKey = rightMetric?.replace(/^(usage|loads):/, "");
    const leftSource = leftMetric?.startsWith("usage:") ? "usage" : "loads";
    const rightSource = rightMetric?.startsWith("usage:") ? "usage" : "loads";

    // Get conversion info
    const leftConversion = leftMetric ? needsConversion(leftKey, leftSource) : null;
    const rightConversion = rightMetric ? needsConversion(rightKey, rightSource) : null;

    // Get data arrays
    const leftData = leftMetric
        ? leftSource === "usage"
            ? usageData[leftKey] || []
            : loadsTempsData[leftKey] || []
        : null;
    const rightData = rightMetric
        ? rightSource === "usage"
            ? usageData[rightKey] || []
            : loadsTempsData[rightKey] || []
        : null;

    // Build chart data array - Google Charts format
    const headers = ["Hour"];
    if (leftMetric) headers.push(leftKey);
    if (rightMetric) headers.push(rightKey);
    const chartData = [headers];

    hours.forEach((hour) => {
        const row = [hour];
        if (leftMetric && leftData && leftData[hour] !== undefined) {
            let value = parseFloat(leftData[hour]);
            // Apply conversion if needed
            if (leftConversion?.convert) {
                value = leftConversion.convert(value);
            }
            row.push(value);
        } else if (leftMetric) {
            row.push(null);
        }
        if (rightMetric && rightData && rightData[hour] !== undefined) {
            let value = parseFloat(rightData[hour]);
            // Apply conversion if needed
            if (rightConversion?.convert) {
                value = rightConversion.convert(value);
            }
            row.push(value);
        } else if (rightMetric) {
            row.push(null);
        }
        chartData.push(row);
    });

    return chartData;
};
